/**
 * Evolution service
 */

import api from './api';

export type EvolutionStatusResponse = {
  success: boolean;
  data?: any;
  error?: string;
};

export type EvolutionReportResponse = {
  success: boolean;
  data?: any;
  error?: string;
};

export type VersionsResponse = {
  success: boolean;
  data: any[];
  error?: string;
  result?: any;
  versions?: any[];
};

export type ABTestStatusResponse = {
  success: boolean;
  data?: any;
  error?: string;
};

function withData<T extends { success: boolean; data?: any; error?: string; result?: any }>(
  response: T,
  fallback: any
): T {
  const payload = response?.data ?? response?.result ?? response ?? fallback;
  const data = Array.isArray(fallback)
    ? (Array.isArray(payload) ? payload : fallback)
    : (
        payload && typeof payload === 'object' && !Array.isArray(payload)
          ? { ...fallback, ...payload }
          : fallback
      );
  return {
    ...response,
    success: response?.success !== false,
    data,
  };
}

export async function getEvolutionStatus(): Promise<EvolutionStatusResponse> {
  const response = await api.get<EvolutionStatusResponse>('/juben/evolution/status');
  return withData(response, {
    evolution_triggers: [],
    active_ab_tests: [],
    agent_statistics: {},
    optimizer_running: false,
  });
}

export async function triggerEvolution(agent_name: string): Promise<EvolutionStatusResponse> {
  const response = await api.post<EvolutionStatusResponse>('/juben/evolution/trigger', { agent_name });
  return withData(response, { agent_name, queued: false, degraded: true });
}

export async function getVersions(agent_name: string, status?: string): Promise<VersionsResponse> {
  const params: Record<string, string> = { agent_name };
  if (status) params.status = status;
  const response = await api.get<VersionsResponse>('/juben/evolution/versions', params);
  const payload = response?.data ?? response?.result ?? response;
  const data = response?.versions ?? (payload as any)?.versions ?? payload;
  return {
    ...response,
    success: response?.success !== false,
    data: Array.isArray(data) ? data : [],
  };
}

export async function getABTestStatus(agent_name: string): Promise<ABTestStatusResponse> {
  const response = await api.get<ABTestStatusResponse>('/juben/evolution/ab/test', { agent_name });
  return withData(response, {
    agent_name,
    config: null,
    traffic_stats: {},
    performance_comparison: {},
    degraded: true,
  });
}

export async function promoteVersion(agent_name: string, version_id: string): Promise<EvolutionStatusResponse> {
  const response = await api.post<EvolutionStatusResponse>('/juben/evolution/ab/promote', { agent_name, version_id });
  return withData(response, { agent_name, promoted_version: version_id, degraded: true });
}

export async function getEvolutionReport(date?: string): Promise<EvolutionReportResponse> {
  const params: Record<string, string> = {};
  if (date) params.date = date;
  const response = await api.get<EvolutionReportResponse>('/juben/evolution/report', params);
  return withData(response, {
    date,
    degraded: true,
    summary: '暂无进化报告',
    evolutions: [],
    metrics: {},
  });
}
